import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import { MapPin, DollarSign, Languages, Calendar, Clock, ArrowRight, Briefcase, Search, Eye, FileText, Video, Play } from 'lucide-react';

const ProspectDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [analytics, setAnalytics] = useState({
    newInvitations: 1,
    upcomingEvents: 2,
    searchAppearances: 86,
    jobApplications: 49
  });
  const [jobOpportunities, setJobOpportunities] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const tipOfTheDay = "Never answer: I do not know. Instead, tell the hiring manager: I do not know the answer, but I would do the following...";

  useEffect(() => {
    if (userProfile?.id) {
      fetchDashboardData();
    }
  }, [userProfile]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch job opportunities
      const jobsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions`
      );
      if (jobsResponse.data.success) {
        setJobOpportunities(jobsResponse.data.data?.slice(0, 5) || []);
      }

      // Fetch prospect's applications (My Jobs)
      const applicationsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${userProfile.id}/applications`
      );
      let applicationCount = 0;
      if (applicationsResponse.data.success) {
        const apps = applicationsResponse.data.data || [];
        setMyJobs(apps.slice(0, 4));
        applicationCount = apps.length;
      }

      // Fetch upcoming events/interviews
      const eventsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${userProfile.id}/upcoming-events`
      );
      let eventCount = 0;
      if (eventsResponse.data.success) {
        const events = eventsResponse.data.data || [];
        setUpcomingEvents(events);
        eventCount = events.length;
      }

      // Update analytics with real data
      setAnalytics({
        newInvitations: eventCount,
        upcomingEvents: eventCount,
        searchAppearances: 0, // This would need a separate tracking system
        jobApplications: applicationCount
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Analytics & Events */}
          <div className="lg:col-span-4 space-y-6">
            {/* Analytics Section */}
            <div>
              <div className="flex items-baseline gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Analytics</h2>
                <span className="text-sm text-gray-500">
                  {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {analytics.newInvitations}
                  </div>
                  <div className="text-xs text-gray-600">new invitation</div>
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {analytics.upcomingEvents}
                  </div>
                  <div className="text-xs text-gray-600">upcoming events</div>
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {analytics.searchAppearances}
                  </div>
                  <div className="text-xs text-gray-600 leading-tight">appearances<br/>in the search</div>
                </div>
                
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {analytics.jobApplications}
                  </div>
                  <div className="text-xs text-gray-600 leading-tight">
                    job applications<br/>in {new Date().toLocaleDateString('en-US', { month: 'long' })}
                  </div>
                </div>
              </div>
            </div>

            {/* Upcoming Events Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming events</h2>
              
              <div className="space-y-3">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event, idx) => (
                    event.isAsync ? (
                      <div
                        key={idx}
                        className="bg-white rounded-lg border border-purple-200 p-4"
                        data-testid="async-interview-card"
                      >
                        <div className="flex items-center gap-2 text-gray-900 font-medium text-sm mb-2">
                          <Video className="w-4 h-4 text-purple-600" />
                          <span>{event.isOpen ? `Open · ${event.time}` : `${event.date} ${event.time}`}</span>
                        </div>
                        <p className="text-gray-700 text-sm mb-1 font-medium">{event.position}</p>
                        <p className="text-gray-500 text-xs mb-2">{event.company}</p>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-block px-3 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {event.questionCount} question{event.questionCount !== 1 ? 's' : ''} · Video
                          </span>
                        </div>
                        {event.isOpen ? (
                          <a
                            href={`/async-interview/${event.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium px-3 py-1.5 rounded-md"
                            data-testid="async-start-button"
                          >
                            <Play className="w-3 h-3" />
                            Start interview
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <Clock className="w-3 h-3" /> Opens {event.date} {event.time}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div
                        key={idx}
                        className="bg-white rounded-lg border border-gray-200 p-4"
                      >
                        <div className="flex items-center gap-2 text-gray-900 font-medium text-sm mb-2">
                          <Calendar className="w-4 h-4" />
                          <span>{event.date} {event.time}</span>
                        </div>
                        <p className="text-gray-700 text-sm mb-2 underline cursor-pointer">{event.company}</p>
                        <span className={`inline-block px-3 py-1 rounded text-xs font-medium ${
                          event.type === 'video_call'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-teal-100 text-teal-700'
                        }`}>
                          {event.label}
                        </span>
                        {event.meetLink && (
                          <a
                            href={event.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mt-2 text-blue-600 hover:underline text-xs"
                          >
                            Join Google Meet
                          </a>
                        )}
                      </div>
                    )
                  ))
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                    <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No upcoming events</p>
                    <p className="text-gray-400 text-xs mt-1">Interview invitations will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle Column - Job Opportunities */}
          <div className="lg:col-span-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Job opportunities</h2>
              <button 
                onClick={() => navigate('/jobs')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                see all
              </button>
            </div>
            
            <div className="space-y-3">
              {jobOpportunities.slice(0, 4).map((job) => (
                <div 
                  key={job.id}
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <h3 className="text-base font-semibold text-blue-600 mb-1">
                    {job.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {job.company_name || job.organization_name}
                  </p>
                  
                  <div className="space-y-1 text-xs text-gray-600">
                    {job.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>{job.location} {job.workplace && `(${job.workplace})`}</span>
                      </div>
                    )}
                    
                    {job.salary_min && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>€ {job.salary_min.toLocaleString()}</span>
                      </div>
                    )}
                    
                    {job.languages && job.languages.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Languages className="w-3 h-3" />
                        <span>{job.languages.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {jobOpportunities.length === 0 && (
                <>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-blue-600 mb-1">Frontend Developer</h3>
                    <p className="text-gray-600 text-sm mb-3">Jupiter GmbH</p>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>Berlin, Germany (Hybrid)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>€ 80,000</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Languages className="w-3 h-3" />
                        <span>English, German</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-blue-600 mb-1">Frontend Developer</h3>
                    <p className="text-gray-600 text-sm mb-3">Saturn Group</p>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>Berlin, Germany (Hybrid)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>€ 80,000</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Languages className="w-3 h-3" />
                        <span>English, German</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-blue-600 mb-1">Frontend Developer</h3>
                    <p className="text-gray-600 text-sm mb-3">Mercury Group</p>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>Berlin, Germany (Hybrid)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>€ 80,000</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Languages className="w-3 h-3" />
                        <span>English, German</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="text-base font-semibold text-blue-600 mb-1">Frontend Developer</h3>
                    <p className="text-gray-600 text-sm mb-3">Mars Group</p>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>Berlin, Germany (Hybrid)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-3 h-3" />
                        <span>€ 80,000</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Languages className="w-3 h-3" />
                        <span>English, German</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            {/* Tip of the Day */}
            <div className="bg-gradient-to-br from-pink-50 to-blue-50 rounded-lg border border-pink-200 p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Tip of the day</h2>
              <div className="relative">
                <div className="absolute -top-2 -left-2 text-5xl text-blue-200 opacity-50">"</div>
                <p className="text-gray-700 text-sm leading-relaxed relative z-10 pl-4">
                  {tipOfTheDay}
                </p>
              </div>
            </div>

            {/* My Jobs Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">My Jobs</h2>
                <button 
                  onClick={() => navigate('/my-jobs')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  see all
                </button>
              </div>
              
              <div className="space-y-3">
                {myJobs.length > 0 ? (
                  myJobs.map((job) => (
                    <div 
                      key={job.id}
                      className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/jobs/${job.position_id}`)}
                    >
                      <div className="flex items-start gap-2">
                        <Briefcase className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between mb-1">
                            <h3 className="font-semibold text-gray-900 text-sm">
                              {job.positions?.title || 'Position'}
                            </h3>
                            <span className="text-xs text-gray-500 text-right">
                              {new Date(job.applied_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mb-1">
                            {job.positions?.company_name || job.positions?.organizations?.name || 'Company'}
                          </p>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            job.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            job.status === 'reviewing' ? 'bg-blue-100 text-blue-700' :
                            job.status === 'shortlisted' ? 'bg-green-100 text-green-700' :
                            job.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {job.status === 'pending' ? 'Applied' :
                             job.status === 'reviewing' ? 'Under Review' :
                             job.status === 'shortlisted' ? 'Shortlisted' :
                             job.status === 'rejected' ? 'Not Selected' :
                             job.status || 'Applied'}
                          </span>
                          {job.match_score !== null && job.match_score !== undefined && (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ml-1 ${
                              job.match_score >= 75 ? 'bg-green-100 text-green-700' :
                              job.match_score >= 50 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`} data-testid="my-job-match-score">
                              {job.match_score}% Match
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                    <Briefcase className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No job applications yet</p>
                    <button 
                      onClick={() => navigate('/jobs')}
                      className="mt-2 text-blue-600 hover:underline text-xs"
                    >
                      Browse open positions
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProspectDashboard;
